import type { TacticalPlanResponseType } from '../../TacticalPlan/TacticalPlanApi';
import type { VehicleResponseType } from '../../Vehicles/types';
import type { AssignmentResponseType } from '../AssignmentApi';
import type { ScenarioResponseType } from '../ScenarioTypes';
import { Vehicle } from '../../Vehicles/Vehicle';
import { Assignment } from '../Assignment';
import { StageTargetProcessingChecker } from './StageTargetProcessingChecker/StageTargetProcessingChecker';
import { RouteInfoUtils } from '../PageDeliveries2/RouteSummaryApp/RouteInfoUtils';
import type { TargetStageTypes } from '../TargetStage';
import type { ScenarioToCompareResponseType } from '../ScenarioApi';
import { QuickCategories } from '../QuickCategories/QuickCategories';
import {
    quickCategoriesConfig,
    type QuickCategoriesType,
} from '../QuickCategories/QuickCategoriesConfig';

export class AssignmentManager {
    private scenarioSections: Assignment[] = [];
    private showLocked: boolean = true;
    private showUnlocked: boolean = true;
    private vehicles: VehicleResponseType[] = [];
    private scenario: ScenarioResponseType | null = null;
    private allScenarios: ScenarioResponseType[] | null = null;
    private tacticalPlan: TacticalPlanResponseType | null = null;
    private stageTargetProcessingChecker: StageTargetProcessingChecker | null = null;

    public setStageTargetProcessingChecker(
        stageTargetProcessingChecker: StageTargetProcessingChecker
    ) {
        this.stageTargetProcessingChecker = stageTargetProcessingChecker;
    }

    public getDistanceMeters(): number | null {
        // total sum from all assignments
        let totalDistanceMeters = 0;
        for (let assignment of this.scenarioSections) {
            if (assignment.isRouteCalculated()) {
                totalDistanceMeters += assignment.getRouteDistance('m') || 0;
            }
        }
        return totalDistanceMeters;
    }

    public getDistanceMetersToCompare(): number | null {
        // total sum from all assignments
        let totalDistanceMeters = 0;
        for (let assignment of this.scenarioSections) {
            if (assignment.toCompare()?.isRouteCalculated()) {
                totalDistanceMeters += assignment.toCompare()?.getRouteDistance('m') || 0;
            }
        }
        return totalDistanceMeters;
    }

    public getDurationSecondsToCompare(): number | null {
        // total sum from all assignments
        let totalDurationSeconds = 0;
        for (let assignment of this.scenarioSections) {
            if (assignment.toCompare()?.isRouteCalculated()) {
                totalDurationSeconds += assignment.toCompare()?.getRouteDuration('s') || 0;
            }
        }
        return totalDurationSeconds;
    }

    public getRouteCostToCompare(): number | null {
        let totalCost = 0;
        for (let assignment of this.scenarioSections) {
            if (assignment.toCompare()?.isRouteCalculated()) {
                let distance = assignment.toCompare()?.getRouteDistance('m') || 0;
                let duration = assignment.toCompare()?.getRouteDuration('s') || 0;
                let quantity =
                    assignment.toCompare()?.getCountDeliveries() +
                        assignment.toCompare()?.getCountRealisations() || 0;
                let tolls = assignment.toCompare()?.getRouteTolls();
                totalCost +=
                    RouteInfoUtils.getCostByDistanceAndQuantity(distance, quantity, duration, tolls, assignment.toCompare()?.getVehicle()) ?? 0;
            }
        }
        return totalCost;
    }

    public getRouteCostDetailsToCompare(): Record<string,(number|null)[]> {
        let totalCostDetails: Record<string,(number|null)[]> = {};
        for (let assignment of this.scenarioSections) {
            if (assignment.toCompare()?.isRouteCalculated()) {
                let distance = assignment.toCompare()?.getRouteDistance('m') || 0;
                let duration = assignment.toCompare()?.getRouteDuration('s') || 0;
                let quantity =
                    assignment.toCompare()?.getCountDeliveries() +
                        assignment.toCompare()?.getCountRealisations() || 0;
                let tolls = assignment.toCompare()?.getRouteTolls();
                let assignmentCostDetails = {};
                RouteInfoUtils.getCostByDistanceAndQuantity(distance, quantity, duration, tolls, assignment.toCompare()?.getVehicle(), assignmentCostDetails) ?? 0;
                for (let i in assignmentCostDetails) {
                    if (!totalCostDetails[i]) {
                        totalCostDetails[i] = [0, 0];
                    }
                    totalCostDetails[i][0] += (assignmentCostDetails[i][0] ?? 0);
                    totalCostDetails[i][1] += (assignmentCostDetails[i][1] ?? 0);
                }
            }
        }
        return totalCostDetails;
    }

    public getDurationSeconds(): number | null {
        // total sum from all assignments
        let totalDurationSeconds = 0;
        for (let assignment of this.scenarioSections) {
            if (assignment.isRouteCalculated()) {
                totalDurationSeconds += assignment.getRouteDuration('s') || 0;
            }
        }
        return totalDurationSeconds;
    }

    public getRouteCost(): number | null {
        let totalCost = 0;
        for (let assignment of this.scenarioSections) {
            if (assignment.isRouteCalculated()) {
                let distance = assignment.getRouteDistance('m') || 0;
                let duration = assignment.getRouteDuration('s') || 0;
                let quantity =
                    assignment.getCountDeliveries() + assignment.getCountRealisations() || 0;
                let tolls = assignment.getRouteTolls();
                totalCost +=
                    RouteInfoUtils.getCostByDistanceAndQuantity(distance, quantity, duration, tolls, assignment.getVehicle()) ?? 0;
            }
        }
        return totalCost;
    }

    public getRouteCostDetails(): Record<string,(number|null)[]> {
        let totalCostDetails: Record<string,(number|null)[]> = {};
        for (let assignment of this.scenarioSections) {
            if (assignment.isRouteCalculated()) {
                let distance = assignment.getRouteDistance('m') || 0;
                let duration = assignment.getRouteDuration('s') || 0;
                let quantity =
                    assignment.getCountDeliveries() + assignment.getCountRealisations() || 0;
                let tolls = assignment.getRouteTolls();
                let assignmentCostDetails = {};
                RouteInfoUtils.getCostByDistanceAndQuantity(distance, quantity, duration, tolls, assignment.getVehicle(), assignmentCostDetails) ?? 0;
                for (let i in assignmentCostDetails) {
                    if (!totalCostDetails[i]) {
                        totalCostDetails[i] = [0, 0];
                    }
                    totalCostDetails[i][0] += (assignmentCostDetails[i][0] ?? 0);
                    totalCostDetails[i][1] += (assignmentCostDetails[i][1] ?? 0);
                }
            }
        }
        return totalCostDetails;
    }

    public clear(): void {
        this.scenarioSections = [];
        this.vehicles = [];
        // this.vehiclesClass = [];
        this.scenario = null;
        this.tacticalPlan = null;
    }

    public updateAssignmentAdhoc(assignment_id: number, adhoc_count: number) {
        this.scenarioSections.forEach(assignment => {
            if (assignment.getId() === assignment_id) {
                assignment.setAdhocCount(adhoc_count);
            }
        });
    }

    public updateAssignment(assignment: AssignmentResponseType): void {
        let updated: boolean = false;
        this.scenarioSections.forEach(scenarioSection => {
            if (scenarioSection.getId() === assignment.id) {
                scenarioSection.set(assignment);
                updated = true;
            }
        });
        if (!updated) {
            console.warn('$deliveryList.currentAssignment, adding new assignment', assignment);
            this.addScenarioSection(new Assignment(assignment));
        }
    }

    public updateSection(assignment: Assignment): void {
        let updated: boolean = false;
        this.scenarioSections.forEach(scenarioSection => {
            if (scenarioSection.getId() === assignment.getId()) {
                scenarioSection.set(assignment.get());
                updated = true;
            }
        });
        if (!updated) {
            this.addScenarioSection(assignment);
        }
    }

    public isPlanningStarted(): boolean {
        return true;
        // return get(planning).planningStarted;
    }

    public getDay(): string {
        return this.scenario?.day || '';
    }

    public addScenarioSection(assignment: Assignment) {
        this.scenarioSections.push(assignment);
    }

    public getScenarioSections(
        useLockedFilter: boolean = true,
        showAdHocAssignment: boolean = false,
        showOnlyAssignmentsWithAdhocs: boolean = false
    ): Assignment[] {
        if (useLockedFilter) {
            return this.scenarioSections
                .filter(assignment => {
                    (this.showLocked && assignment.isLocked()) ||
                        (this.showUnlocked && !assignment.isLocked());
                })
                .filter(
                    assignment =>
                        (showAdHocAssignment && assignment.isAdHoc()) || !assignment.isAdHoc()
                );
            // .filter(assignment => !showOnlyAssignmentsWithAdhocs || assignment.getAdhocCount() > 0);
        }
        return this.scenarioSections
            .filter(assignment => {
                return (showAdHocAssignment && assignment.isAdHoc()) || !assignment.isAdHoc();
            })
            .filter(assignment => !showOnlyAssignmentsWithAdhocs || assignment.getAdhocCount() > 0);
    }

    public setScenario(scenario: ScenarioResponseType): void {
        this.scenario = scenario;
    }

    public getScenario(): ScenarioResponseType | null {
        return this.scenario;
    }

    public setAllScenarios(scenarios: ScenarioResponseType[]): void {
        this.allScenarios = scenarios;
    }

    public getAllScenarios(): ScenarioResponseType[] | null {
        return this.allScenarios;
    }

    public getAllScenariosToCompare(): ScenarioResponseType[] {
        if (!this.scenario || !this.allScenarios) return [];
        return this.allScenarios.filter(scenario => {
            return scenario.id !== this.scenario?.id;
        });
    }

    public setTacticalPlan(tacticalPlan: TacticalPlanResponseType | null): void {
        this.tacticalPlan = tacticalPlan;
    }

    public getScenarioId(): number | null {
        return this.scenario?.id || null;
    }

    public getTacticalPlanId(): number | null {
        return this.tacticalPlan?.id || null;
    }

    public getTacticalPlanName(): string {
        return this.tacticalPlan?.name || '(brak nazwy)';
    }

    public orderByPriority(): this {
        this.orderById();
        this.orderByAssigmentType();
        this.orderByVehicleAssignment();
        this.orderByVehicleName();
        return this;
    }

    public orderByVehicleName(): this {
        this.scenarioSections.sort((a, b) => {
            if (a.getVehicleName() > b.getVehicleName()) {
                return 1;
            }
            if (a.getVehicleName() < b.getVehicleName()) {
                return -1;
            }
            return 0;
        });
        return this;
    }

    public setVehicles(vehicles: VehicleResponseType[]): void {
        this.vehicles = vehicles;
        this.scenarioSections.forEach(scenarioSection => {
            let vehicle = vehicles.find(vehicle => {
                return vehicle.id === scenarioSection.getVehicleId();
            });
            if (vehicle) scenarioSection.setVehicle(vehicle);
        });
    }

    public getVehiclesData(): VehicleResponseType[] {
        return this.vehicles;
    }

    public getVehiclesWithColors(vehicleIds: number[] | null = null): Vehicle[] {
        return this.vehicles
            .filter(vehicle => vehicleIds === null || vehicleIds.includes(vehicle.id))
            .map(vehicle => {
                let color = this.scenarioSections.find(assignment => {
                    return assignment.getVehicleId() === vehicle.id;
                });
                return new Vehicle().set(vehicle).setColor(color ? color.getColor() : '');
            });
    }

    public orderById(): void {
        this.scenarioSections.sort((a, b) => {
            if (a.getId() > b.getId()) {
                return 1;
            }
            if (a.getId() < b.getId()) {
                return -1;
            }
            return 0;
        });
    }

    public orderByAssigmentType(): void {
        this.scenarioSections.sort((a, b) => {
            if (a.isDefault()) {
                return 1;
            }
            if (b.isDefault()) {
                return -1;
            }
            return 0;
        });
    }

    public orderByVehicleAssignment(): void {
        this.scenarioSections.sort((a, b) => {
            if (a.hasVehicleAssigned() && !b.hasVehicleAssigned()) {
                return 1;
            }
            if (!a.hasVehicleAssigned() && b.hasVehicleAssigned()) {
                return -1;
            }
            return 0;
        });
    }

    public orderByCallback(callback: (a: Assignment, b: Assignment) => number) {
        this.scenarioSections.sort(callback);
    }

    public setVisibleLocked(showLocked: boolean): void {
        this.showLocked = showLocked;
    }

    public setVisibleUnlocked(showUnlocked: boolean): void {
        this.showUnlocked = showUnlocked;
    }

    public getCountRoutesToCalculate(): number {
        return this.getIdsRoutesToCalculate().length;
    }

    public getIdsRoutesToCalculate(): number[] {
        return this.scenarioSections
            .filter(assignment => {
                return (
                    !assignment.isLocked() &&
                    assignment.canRouteBeCalculated() &&
                    !assignment.isRouteCalculated()
                );
            })
            .map(assignment => {
                return assignment.getId();
            });
    }

    public getIdsRoutesToCalculateWithAccept(): number[] {
        return this.scenarioSections
            .filter(assignment => {
                return (
                    !assignment.isLocked() &&
                    assignment.canRouteBeCalculatedWithAccept() &&
                    !assignment.isRouteCalculated()
                );
            })
            .map(assignment => {
                return assignment.getId();
            });
    }

    public getRoutesToLockIds(): number[] {
        return this.scenarioSections
            .filter(assignment => {
                return (
                    !assignment.isLocked() &&
                    assignment.isRouteCalculated() &&
                    assignment.hasVehicleAssigned()
                );
                // return !assignment.isLocked() && assignment.hasVehicleAssigned() && (assignment.getCountDeliveries() > 0 || assignment.getCountSuggestedJobs() > 0);
            })
            .map(assignment => {
                return assignment.getId();
            });
    }

    public getRoutesToLockIdsWithAccept(): number[] {
        return this.scenarioSections
            .filter(assignment => {
                return !assignment.isLocked() && assignment.canRouteBeCalculatedWithAccept();
            })
            .map(assignment => {
                return assignment.getId();
            });
    }

    public getAssignmentsIdsEnabledToAccept(): number[] {
        return this.scenarioSections
            .filter(assignment => {
                return assignment.getCountSuggestedJobs() > 0; //  || assignment.getCountDeliveries()>0;
            })
            .map(assignment => {
                return assignment.getId();
            });
    }

    public getAssignmentIdByVehicleId(vehicleId: number | null): number | null {
        if (vehicleId === null) return null;
        let assignment = this.scenarioSections.find(assignment => {
            return assignment.getVehicleId() === vehicleId;
        });
        return assignment ? assignment.getId() : null;
    }

    public getAlreadyUsedVehiclesIds(): number[] {
        return this.scenarioSections
            .filter(assignment => {
                return assignment.hasVehicleAssigned() && assignment.getCountDeliveries() > 0;
            })
            .map(assignment => {
                return assignment.getVehicleId();
            });
    }

    public getAssignmentsIdsEnabledToCalculateRoute(): number[] {
        return this.scenarioSections
            .filter(assignment => {
                return assignment.canRouteBeCalculated();
            })
            .map(assignment => {
                return assignment.getId();
            });
    }

    public getAssignmentsIdsWithDeliveriesToAccept(): number[] {
        return this.scenarioSections
            .filter(assignment => {
                return (
                    assignment.getCountDeliveries() > 0 || assignment.getCountSuggestedJobs() > 0
                );
            })
            .map(assignment => {
                return assignment.getId();
            });
    }

    public getAssignmentsWithDeliveriesToAccept(): Assignment[] {
        return this.scenarioSections.filter(assignment => {
            return assignment.getCountDeliveries() > 0 || assignment.getCountSuggestedJobs() > 0;
        });
    }

    public getVehicleIdByAssignmentId(assignmentId: number): number | null {
        let assignment = this.scenarioSections.find(assignment => {
            return assignment.getId() === assignmentId;
        });
        return assignment ? assignment.getVehicleId() : null;
    }

    public getAssignmentIdByVehicleName(vehicleName: string): number | null {
        let vehicle = this.vehicles.find(vehicle => {
            return vehicle.name === vehicleName;
        });
        if (!vehicle) return null;
        return this.getAssignmentIdByVehicleId(vehicle.id);
    }

    public getAssignmentById(assignmentId: number): Assignment | null {
        if (typeof assignmentId !== 'number') throw new Error('assignmentId is not a number');
        return (
            this.scenarioSections.find(assignment => {
                return assignment.getId() === assignmentId;
            }) || null
        );
    }

    public setColor(assignmentId: number, color: string): void {
        this.scenarioSections.forEach(assignment => {
            if (assignment.getId() === assignmentId) {
                assignment.setColor(color);
            }
        });
    }

    public setAssignmentBusyStatus(assignmentId: number, busy: boolean): void {
        this.scenarioSections.forEach(assignment => {
            // console.log('setBusy, forEach', assignment.getId(), assignmentId, busy);
            if (assignment.getId() === assignmentId) {
                // console.log('setBusy, setBusy', assignmentId, busy);
                assignment.setBusy(busy);
            }
        });
        if (this.stageTargetProcessingChecker) {
            if (busy) {
                this.stageTargetProcessingChecker.addAssignment(assignmentId);
            } else {
                this.stageTargetProcessingChecker.removeAssignment(assignmentId);
            }
        }
    }

    public setAssignmentTargetStage(assignmentId: number, targetStage: TargetStageTypes): void {
        this.scenarioSections.forEach(assignment => {
            if (assignment.getId() === assignmentId) {
                assignment.setTargetStage(targetStage);
            }
        });
    }

    public isAnyAssignmentBusy(): boolean {
        return this.scenarioSections.some(assignment => {
            return assignment.isBusy();
        });
    }

    public getStageTargetProcessingChecker(): StageTargetProcessingChecker | null {
        return this.stageTargetProcessingChecker;
    }

    public setScenarioToCompare(scenarioToCompare: ScenarioToCompareResponseType | null): void {
        // Clear all existing compare assignments
        this.scenarioSections.forEach(assignment => {
            assignment.setToCompare(null);
        });

        // If scenarioToCompare is null, just clear and return
        if (!scenarioToCompare) {
            return;
        }

        // Create a map of vehicle_id -> Assignment from scenarioToCompare
        const compareAssignmentsMap = new Map<number, Assignment>();
        scenarioToCompare.assignments.forEach(assignmentResponse => {
            const vehicleId = assignmentResponse.vehicle_id;
            // Ignore assignments without vehicle_id or with null vehicle_id
            if (vehicleId !== null && vehicleId !== undefined) {
                compareAssignmentsMap.set(vehicleId, new Assignment(assignmentResponse));
            }
        });

        // Change colors for toCompare quick categories
        let toCompateQuickCategoryConfig: QuickCategoriesType[] = quickCategoriesConfig.map(
            category => {
                return {
                    ...category,
                    color: '#4978e2',
                };
            }
        );

        // Match assignments by vehicle_id
        this.scenarioSections.forEach(assignment => {
            const vehicleId = assignment.getVehicleId();
            if (vehicleId !== null && vehicleId !== undefined) {
                const compareAssignment = compareAssignmentsMap.get(vehicleId);
                if (compareAssignment) {
                    let quickCategories = new QuickCategories(toCompateQuickCategoryConfig);
                    let v = assignment.getVehicle();
                    if (v) compareAssignment.setVehicle(v);
                    compareAssignment.setQuickCategories(quickCategories);
                    compareAssignment.setTargetStage('calculate');
                    assignment.setToCompare(compareAssignment);
                }
            }
        });
    }
}
